/**
 * A salesperson's day, and where a new site visit fits in it —
 * `docs/new-request.md` §10.2–§10.8, T8, T13.
 *
 * The server builds the `DayPlan` (stops, drive legs, distances — none of
 * which the browser works out, rule 2) and both sides run these functions over
 * it: the server to find the openings and grade them, the sheet to say
 * whether the block somebody just dragged is OK, tight or in the way. One rule
 * for both, so the strip and the green block cannot disagree.
 *
 * Times are **minutes after midnight in the branch's time zone**. A day is a
 * row of points: the start location at `workStart`, the person's busy blocks
 * in order, and the start location again at `workEnd`. A gap is the time
 * between two consecutive points.
 */
import { ceil15, gradeFor, MARGIN_MINUTES } from './grade.js';
import { clock, clockBare, clockRange, milesWords, minutesWords } from './words.js';
/** Point k's time, as the gap before it would see it. */
function pointEnd(plan, k) {
    return k === 0 ? plan.workStart : plan.stops[k - 1].end;
}
function pointStart(plan, k) {
    return k === plan.stops.length + 1 ? plan.workEnd : plan.stops[k - 1].start;
}
/** Every gap in the day, measured for a visit of `minutes`. */
export function gapsOf(plan, minutes) {
    const gaps = [];
    for (let k = 0; k <= plan.stops.length; k++) {
        const from = pointEnd(plan, k);
        const to = pointStart(plan, k + 1);
        const leg1 = plan.legs?.toP[k] ?? null;
        const leg2 = plan.legs?.fromP[k] ?? null;
        const base = plan.legs?.between[k] ?? null;
        const free = Math.max(0, to - from);
        if (plan.off || !leg1 || !leg2 || !base) {
            gaps.push({ index: k, from, to, free, leg1, leg2, base, earliest: null, needs: null,
                fits: false, added: null, grade: null, estimated: false });
            continue;
        }
        let earliest = ceil15(from + leg1.minutes + MARGIN_MINUTES);
        if (plan.notBefore !== null)
            earliest = Math.max(earliest, plan.notBefore);
        const needs = leg1.minutes + minutes + leg2.minutes + 2 * MARGIN_MINUTES;
        const fits = earliest + minutes + leg2.minutes + MARGIN_MINUTES <= to;
        const added = Math.max(0, leg1.minutes + leg2.minutes - base.minutes);
        gaps.push({
            index: k, from, to, free, leg1, leg2, base, earliest, needs, fits, added,
            grade: fits ? gradeFor(added) : null,
            estimated: leg1.estimated || leg2.estimated || base.estimated,
        });
    }
    return gaps;
}
/** One opening per gap that fits, at its earliest start (§10.2). */
export const openingsOf = (plan, minutes) => gapsOf(plan, minutes).filter((gap) => gap.fits);
/** Best grade, then the smallest Δ, then the earliest (§10.4). */
export function compareOpenings(a, b) {
    const rank = (grade) => grade === 'best' ? 0 : grade === 'good' ? 1 : grade === 'ok' ? 2 : 3;
    return rank(a.grade) - rank(b.grade)
        || (a.added ?? 999) - (b.added ?? 999)
        || (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)
        || (a.earliest ?? 0) - (b.earliest ?? 0);
}
/**
 * An opening that ends within an hour of the day's end is *the last stop of
 * the day*; one with afternoon still in front of it is *after* the stop
 * before it. Not drawn as a rule — derived from Board 7's two examples.
 */
const LAST_STOP_WITHIN = 60;
export function reasonFor(plan, gap, minutes) {
    const before = gap.index > 0 ? plan.stops[gap.index - 1] : undefined;
    const after = gap.index < plan.stops.length ? plan.stops[gap.index] : undefined;
    if (before && after) {
        return { kind: 'between', a: before.label, aMiles: before.miles, b: after.label, bMiles: after.miles };
    }
    if (!before) {
        /* The next stop's distance, or — on a day with none — the start's. */
        return { kind: 'first', miles: after ? after.miles : plan.startMiles };
    }
    const ends = (gap.earliest ?? gap.from) + minutes + (gap.leg2?.minutes ?? 0);
    if (plan.workEnd - ends <= LAST_STOP_WITHIN) {
        return { kind: 'last', a: before.label, aMiles: before.miles };
    }
    return { kind: 'after', a: before.label, aMiles: before.miles };
}
const mi = (miles) => miles === null ? '' : milesWords(miles);
/**
 * The words a reason is said in: `long` on the Best-time cards (Board 7),
 * `short` on a Side by side card, `phone` without distances (Board 16).
 */
export function reasonWords(reason, form = 'long') {
    switch (reason.kind) {
        case 'between':
            if (form === 'short')
                return `${reason.a} → ${reason.b}`;
            if (form === 'phone')
                return `between ${reason.a} and ${reason.b}`;
            return `between ${reason.a} (${mi(reason.aMiles)}) and ${reason.b} (${mi(reason.bMiles)})`;
        case 'after':
            if (form === 'short')
                return `after a ${mi(reason.aMiles)} stop`;
            if (form === 'phone')
                return `after ${reason.a}`;
            return `after ${reason.a}, ${mi(reason.aMiles)}`;
        case 'first':
            if (form === 'phone')
                return 'first stop';
            return reason.miles === null ? 'first stop' : `first stop, ${mi(reason.miles)}`;
        case 'last':
            if (form === 'short')
                return `last stop, ${mi(reason.aMiles)}`;
            if (form === 'phone')
                return 'last stop of the day';
            return `last stop of the day, ${mi(reason.aMiles)} from ${reason.a}`;
    }
}
/**
 * Where a visit starting at `start` for `minutes` sits in the day, and what
 * to say about it. The margins are the same 5 minutes the openings keep.
 */
export function placementOf(plan, start, minutes) {
    const end = start + minutes;
    const gaps = gapsOf(plan, minutes);
    const openings = gaps.filter((gap) => gap.fits && gap.earliest !== null);
    const moveTo = openings.length === 0 ? null
        : openings.reduce((best, gap) => Math.abs(gap.earliest - start) < Math.abs(best.earliest - start) ? gap : best).earliest;
    /* The gap is the last one that begins at or before the start. */
    let k = 0;
    for (let index = 0; index < plan.stops.length; index++) {
        if (plan.stops[index].start <= start)
            k = index + 1;
    }
    const gap = gaps[k];
    const beforeStop = k > 0 ? plan.stops[k - 1] : null;
    const afterStop = k < plan.stops.length ? plan.stops[k] : null;
    const before = gap.leg1 ? {
        label: beforeStop ? beforeStop.label : plan.startName,
        drive: gap.leg1,
        spare: start - gap.from - gap.leg1.minutes,
    } : null;
    const after = gap.leg2 && afterStop ? {
        label: afterStop.label,
        at: afterStop.start,
        drive: gap.leg2,
        spare: afterStop.start - end - gap.leg2.minutes,
    } : null;
    const overlaps = plan.stops.some((stop) => stop.start < end && start < stop.end);
    let state;
    if (plan.off)
        state = 'off';
    else if (start < plan.workStart || end > plan.workEnd)
        state = 'outside';
    else if (overlaps || (before && before.spare < 0) || (after && after.spare < 0))
        state = 'late';
    else if ((before && before.spare < MARGIN_MINUTES) || (after && after.spare < MARGIN_MINUTES)) {
        state = 'tight';
    }
    else
        state = 'ok';
    return { state, start, end, before, after, moveTo, gap: k };
}
/** *about 6 min* for an estimate, *6 min* for Google's. */
export const driveWords = (leg) => leg.estimated ? `about ${minutesWords(leg.minutes)}` : minutesWords(leg.minutes);
/**
 * The When box's line for an OK placement (Board 7):
 * *8 min from Gallagher before · 6 min to Fox Hollow after — enough time both ways*.
 */
export function okWords(placement) {
    const parts = [];
    if (placement.before)
        parts.push(`${driveWords(placement.before.drive)} from ${placement.before.label} before`);
    if (placement.after)
        parts.push(`${driveWords(placement.after.drive)} to ${placement.after.label} after`);
    return `${parts.join(' · ')} — enough time both ways`;
}
/** The amber band's two lines for a tight placement (Board 8 A). */
export function tightWords(placement) {
    const after = placement.after;
    if (after && after.spare < MARGIN_MINUTES) {
        const room = after.at - placement.end;
        return {
            title: `Tight before ${after.label} at ${clockBare(after.at)}.`,
            body: `Ending at ${clockBare(placement.end)} leaves ${room} min for a ${after.drive.minutes} min drive — less than the ${MARGIN_MINUTES} min margin.`,
        };
    }
    const before = placement.before;
    const room = before.spare + before.drive.minutes;
    return {
        title: `Tight after ${before.label}.`,
        body: `Starting at ${clockBare(placement.start)} leaves ${room} min for a ${before.drive.minutes} min drive — less than the ${MARGIN_MINUTES} min margin.`,
    };
}
/** The red band's two lines (Board 8 B). */
export function lateWords(placement, plan, firstName) {
    const range = clockRange(placement.start, placement.end, true);
    const crossing = plan.stops.find((stop) => stop.start < placement.end && placement.start < stop.end)
        ?? (placement.after && placement.after.spare < 0
            ? plan.stops.find((stop) => stop.label === placement.after.label) : undefined);
    if (crossing) {
        return {
            title: `${range} runs into ${crossing.label} at ${clockBare(crossing.start)}.`,
            body: `${firstName} can’t be in two places — move it, or book it anyway if ${crossing.label} has already been moved.`,
        };
    }
    const before = placement.before;
    return {
        title: `${range} starts before ${firstName} can get there from ${before?.label ?? plan.startName}.`,
        body: `${firstName} can’t be in two places — move it, or book it anyway if ${before?.label ?? plan.startName} has already been moved.`,
    };
}
/** *Move to 10:00 AM*. */
export const moveToWords = (minutes) => `Move to ${clock(minutes)}`;
export function daySummary(plan, placement) {
    const legs = plan.legs;
    const n = plan.stops.length;
    if (!legs) {
        const last = n > 0 ? plan.stops[n - 1].end : plan.workStart;
        return { appointments: n + (placement ? 1 : 0), driving: 0, added: 0, estimated: true,
            driveBefore: null, driveAfter: null, backAt: placement ? Math.max(last, placement.end) : last };
    }
    const base = legs.between.reduce((sum, leg) => sum + leg.minutes, 0);
    let estimated = legs.between.some((leg) => leg.estimated);
    if (!placement) {
        const lastEnd = n > 0 ? plan.stops[n - 1].end : plan.workStart;
        return { appointments: n, driving: base, added: 0, estimated, driveBefore: null,
            driveAfter: null, backAt: lastEnd + legs.between[n].minutes };
    }
    const k = placement.gap;
    const leg1 = legs.toP[k];
    const leg2 = legs.fromP[k];
    const driving = base - legs.between[k].minutes + leg1.minutes + leg2.minutes;
    estimated = estimated || leg1.estimated || leg2.estimated;
    const backAt = k === n
        ? placement.end + leg2.minutes
        : plan.stops[n - 1].end + legs.between[n].minutes;
    return {
        appointments: n + 1, driving, added: driving - base, estimated,
        driveBefore: leg1, driveAfter: leg2, backAt,
    };
}
/** *today*, the earliest a same-day visit may start: now + 30 min, on the quarter hour (A2). */
export const sameDayFloor = (nowMinutes) => ceil15(nowMinutes + 30);
/**
 * The card's *Near the property on Thu — a 12:30 stop 1.6 mi away* (§9.2): the
 * nearer of the two appointments an opening sits between, when it is within
 * 3 miles. Only *between* two: Board 6 draws Hannah's first stop of the day
 * — with a stop 2.2 mi after it — with no such line, so an opening at either
 * end of a day is not "near" anything.
 */
export function nearStop(plan, gapIndex) {
    const before = plan.stops[gapIndex - 1];
    const after = plan.stops[gapIndex];
    if (!before || !after)
        return null;
    const sides = [before, after]
        .filter((stop) => Boolean(stop && stop.placed && stop.miles !== null));
    const nearest = sides.sort((a, b) => a.miles - b.miles)[0];
    return nearest && nearest.miles <= NEAR_MILES ? nearest : null;
}
/** Green within this, amber to `FAR_MILES`, grey beyond (§10.5). */
export const NEAR_MILES = 3;
export const FAR_MILES = 8;
export const milesTone = (miles) => miles === null ? 'far' : miles <= NEAR_MILES ? 'near' : miles <= FAR_MILES ? 'mid' : 'far';
//# sourceMappingURL=day.js.map